/**
 * Global overlays — announcement banner + feedback trigger.
 *
 * Mounted once at the app root so every page picks them up without
 * having to import anything. Hidden on /admin/* and /instructor/* to
 * keep those workflows uncluttered.
 */
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { X, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// ─── Announcement banner + popup ────────────────────────────────────
function AnnouncementBanner() {
  const [items, setItems] = useState([]);
  const [dismissed, setDismissed] = useState(() => {
    try { return new Set(JSON.parse(sessionStorage.getItem('sama:ann-dismissed') || '[]')); }
    catch { return new Set(); }
  });

  useEffect(() => {
    fetch(`${API}/api/announcements`)
      .then(r => r.ok ? r.json() : { items: [] })
      .then(d => setItems(d.items || []))
      .catch(() => {});
  }, []);

  const visible = items.filter(a => !dismissed.has(a.id));
  if (visible.length === 0) return null;
  // Show the newest as the primary banner
  const primary = visible[0];

  const dismiss = (id) => {
    const next = new Set(dismissed); next.add(id);
    setDismissed(next);
    try { sessionStorage.setItem('sama:ann-dismissed', JSON.stringify(Array.from(next))); } catch { /* noop */ }
  };

  return (
    <>
      <div
        className="fixed top-0 left-0 right-0 z-40 bg-primary text-primary-foreground px-4 py-2 shadow-md"
        data-testid="announcement-banner"
      >
        <div className="max-w-6xl mx-auto flex items-center gap-3">
          {primary.type === 'image' && primary.images?.[0] ? (
            <div className="flex items-center gap-2 flex-1 overflow-x-auto">
              {(primary.images || []).slice(0, 4).map((src, i) => (
                <img key={i} src={src} alt="announcement" className="h-8 sm:h-10 w-auto rounded" />
              ))}
              <span className="text-xs sm:text-sm truncate flex-1">{primary.title || 'Announcement'}</span>
            </div>
          ) : (
            <div className="flex-1 text-xs sm:text-sm truncate" data-testid="announcement-body">
              <span className="font-semibold mr-2">{primary.title}</span>
              <span className="opacity-90">{primary.description}</span>
            </div>
          )}
          {primary.cta_url && (
            <a
              href={primary.cta_url}
              target={primary.cta_url.startsWith('http') ? '_blank' : undefined}
              rel="noreferrer"
              className="shrink-0 inline-flex items-center px-3 py-1 rounded-full bg-white/15 hover:bg-white/25 text-xs sm:text-sm font-medium transition-colors"
              data-testid="announcement-cta-btn"
            >
              {primary.cta_label || 'Learn more'} →
            </a>
          )}
          <button onClick={() => dismiss(primary.id)} aria-label="Dismiss" className="opacity-80 hover:opacity-100 shrink-0"
            data-testid="announcement-dismiss-btn">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
      {/* leave room so nothing sits under the banner */}
      <div className="h-11 sm:h-12" aria-hidden="true" />
    </>
  );
}


// ─── Feedback trigger + modal ────────────────────────────────────────
function FeedbackWidget() {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', feedback_type: 'Class', message: '' });

  async function submit(e) {
    e.preventDefault();
    if (form.message.trim().length < 3) return toast.error('Please add a bit more detail');
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(form),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || 'Could not submit');
      toast.success(d.message || 'Thank you for your feedback!');
      setOpen(false);
      setForm({ name: '', email: '', feedback_type: 'Class', message: '' });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-20 right-4 z-30 bg-primary text-primary-foreground rounded-full shadow-lg px-4 py-2.5 text-sm font-medium flex items-center gap-1.5 hover:opacity-90"
        data-testid="feedback-trigger"
      >
        <MessageSquare className="w-4 h-4" /> Feedback
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-4"
          onClick={() => setOpen(false)} data-testid="feedback-modal">
          <form
            className="bg-white rounded-2xl max-w-md w-full p-5 sm:p-6 space-y-3"
            onClick={(e) => e.stopPropagation()}
            onSubmit={submit}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Share Feedback</h3>
              <button type="button" onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input type="text" placeholder="Name (optional)" className="rounded-lg border px-3 py-2 text-sm"
                value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                data-testid="feedback-name" />
              <input type="email" placeholder="Email (optional)" className="rounded-lg border px-3 py-2 text-sm"
                value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                data-testid="feedback-email" />
            </div>
            <select className="w-full rounded-lg border px-3 py-2 text-sm"
              value={form.feedback_type} onChange={e => setForm({ ...form, feedback_type: e.target.value })}
              data-testid="feedback-type">
              {['Class', 'Facilities', 'Suggestions', 'Other'].map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <textarea rows={4} placeholder="Your feedback…" className="w-full rounded-lg border px-3 py-2 text-sm"
              value={form.message} onChange={e => setForm({ ...form, message: e.target.value })}
              required data-testid="feedback-message" />
            <button type="submit" disabled={saving}
              className="w-full h-11 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-60"
              data-testid="feedback-submit">
              {saving ? 'Sending…' : 'Send Feedback'}
            </button>
          </form>
        </div>
      )}
    </>
  );
}


export default function GlobalOverlays() {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith('/admin') || pathname.startsWith('/instructor');
  if (isAdmin) return null;

  // Announcement banner is deliberately scoped to /schedule (per PRD):
  // it's a class-schedule surface for members, not a site-wide overlay,
  // so we only mount it there. Feedback widget still shows everywhere
  // outside admin/instructor.
  const showAnnouncement = pathname === '/schedule' || pathname.startsWith('/schedule/');
  return (
    <>
      {showAnnouncement && <AnnouncementBanner />}
      <FeedbackWidget />
    </>
  );
}
